import { type KeyboardEventHandler, type Ref, useCallback } from 'react'
import {
  Snackbar,
  SnackbarAction,
  SnackbarActions,
  SnackbarCloseButton,
  SnackbarContent,
  SnackbarDescription,
  SnackbarHeadline,
  SnackbarIcon,
  SnackbarList,
  SnackbarViewport,
} from '@/components/Snackbar'
import { type Snackbar as SnackbarItem } from '@/features/shell/snackbars/Snackbar'
import { Icon } from '@/components/Icon'
import { observer } from 'mobx-react-lite'
import { Spinner } from '@/components/Spinner'
import { ScreenReaderAnnounce } from '@/components/ScreenReaderAnnounce'
import { withPressHack } from '@/utils/press-hack'
import { usePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import { useFeatures } from '@/contexts/root-store-context'

/**
 * Shows the global snackbars.
 */
export const SnackbarsContainer = observer(function SnackbarsContainer() {
  const {
    internal: { snackbarsManager: manager },
  } = useFeatures(({ shell }) => ({ internal: shell.internal }))

  // We'll announce the latest snackbar (if non-pending).
  const latestSnackbar =
    manager.latest?.type !== 'pending' ? manager.latest : null

  const offset = usePageBottomOffset()
  const latestSnackbarScreenReaderText = [
    latestSnackbar?.title,
    latestSnackbar?.description,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <SnackbarViewport offset={offset}>
      {/* Live regions for urgent and non-urgent snackbars. We'll
      show the latest snackbar in whichever region fits the type. */}
      <ScreenReaderAnnounce
        role="alert"
        text={
          latestSnackbar?.type === 'error' ? latestSnackbarScreenReaderText : ''
        }
      />
      <ScreenReaderAnnounce
        role="status"
        text={
          latestSnackbar?.type !== 'error' ? latestSnackbarScreenReaderText : ''
        }
      />

      <SnackbarList>
        {manager.snackbars.map((snackbar) => (
          <SnackbarContainer key={snackbar.id} snackbar={snackbar} />
        ))}
      </SnackbarList>
    </SnackbarViewport>
  )
})

interface SnackbarContainerProps {
  snackbar: SnackbarItem
  ref?: Ref<HTMLLIElement>
}

/**
 * Snackbar Container
 */
const SnackbarContainer = observer(function SnackbarContainer({
  snackbar,
  ref,
}: SnackbarContainerProps) {
  // If an element within the snackbar receives an Escape key-down event,
  // dismiss the snackbar.
  const keyDown: KeyboardEventHandler = useCallback(
    (e) => e.code === 'Escape' && snackbar.dismiss(),
    [snackbar],
  )

  return (
    <Snackbar
      ref={ref}
      aria-busy={snackbar.type === 'pending'}
      aria-live={snackbar.type === 'error' ? 'assertive' : 'polite'}
      // Manage auto-dismissal based on focus
      onFocus={snackbar.disallowAutoDismiss}
      onBlur={snackbar.allowAutoDismissal}
      // If the element received a click/tap, don't auto dismiss.
      onPointerDown={snackbar.disallowAutoDismiss}
      // Dismiss on Esc.
      onKeyDown={keyDown}
    >
      <SnackbarIconContainer snackbar={snackbar} />

      <SnackbarContent>
        <SnackbarHeadline>{snackbar.title}</SnackbarHeadline>
        {snackbar.description && (
          <SnackbarDescription>{snackbar.description}</SnackbarDescription>
        )}
      </SnackbarContent>

      {snackbar.actions.length > 0 && (
        <SnackbarActions>
          {snackbar.actions.map((action, index) => (
            <SnackbarAction
              label={action.title}
              key={`${action.title}-${index}`}
              onPress={() => snackbar.triggerAction(action)}
            />
          ))}
        </SnackbarActions>
      )}

      <SnackbarCloseButton onPress={withPressHack(snackbar.dismiss)} />
    </Snackbar>
  )
})

/**
 * Renders the proper icon or spinner.
 */
const SnackbarIconContainer = observer(function SnackbarIconContainer({
  snackbar,
}: {
  snackbar: SnackbarItem
}) {
  switch (snackbar.type) {
    case 'neutral':
      return null
    case 'pending':
      return (
        // `key` will make sure enter and exit animations play
        <SnackbarIcon animationKey={snackbar.type}>
          <Spinner size={24} />
        </SnackbarIcon>
      )
    case 'success':
      return (
        <SnackbarIcon animationKey={snackbar.type}>
          <Icon name="tick" />
        </SnackbarIcon>
      )
    case 'warning':
      return (
        <SnackbarIcon animationKey={snackbar.type}>
          <Icon name="alert" />
        </SnackbarIcon>
      )
    case 'error':
      return (
        <SnackbarIcon animationKey={snackbar.type}>
          <Icon name="alert" />
        </SnackbarIcon>
      )
  }
})
